import { cloneDeep } from 'lodash';
import * as THREE from 'three';
import { Mesh } from 'three';
import BaseMesh from './base-mesh';
import { correctVectorsToCommonPlane, getPosition, getWallRoiUvPos, getBorderParkUvPos, getRoiUvPos } from '@blade-sxp/map/utils/roi';
import { getRoiConfig, UVConfig } from '@blade-sxp/map/config/roiConfig';
import { vertexShader, fragmentShader } from '@blade-sxp/map/src/shaders/roiShader';
class ROI3D extends BaseMesh {
    vertices; // 坐标点
    props;
    isAnimation; // 是否开启光墙动画
    timeValue;
    boldLineNum; // 停车位出口边的索引
    opacity; // 0~1;
    normalVector; // 平面法线
    animate; // 动画
    animateId;
    isMouseEnter = false;
    constructor(data) {
        super(data);
        const { points, boldPoints, isAnimation = false, } = data.renderProps;
        this.boldLineNum = this.getBoldLineNum(points, boldPoints);
        this.opacity = { value: data?.opacity ?? 1 };
        this.timeValue = { value: 0.0 };
        this.isAnimation = isAnimation;
        this.position.x = 0;
        this.position.y = 0;
        this.position.z = 0;
        const vectorList = data.renderProps.points.map((item) => {
            // 坐标点列表 高度统一抬高0.3
            const vector = new THREE.Vector3(item[0], item[1] + 0.3, item[2]);
            return vector;
        });
        const { normal, vetices, center } = correctVectorsToCommonPlane(vectorList);
        this.vertices = vetices;
        this.center = center.divideScalar(vetices.length);
        this.normalVector = normal;
        data.renderProps.points = vetices.map((item) => {
            return [item.x, item.y, item.z];
        });
        this.props = data;
        this.update(data, true, true);
        // Animations
        this.animateId = -1;
        this.animate = () => {
            this.animateId = window.requestAnimationFrame(this.animate);
            this.updateTimeVal(data);
        };
        this.animate();
    }
    updateTimeVal(data) {
        if (!this.isAnimation) {
            return;
        }
        this.timeValue.value += 0.005;
        if (this.timeValue.value >= 1.0) {
            this.timeValue.value = 0.0;
        }
    }
    // 可用于小人坐标转化
    getPosition(vertices) {
        // 平面xy对应three中zx
        return getPosition(vertices, this.center, this.normalVector);
    }
    update(data, needsUpdate = false, needUpdateProps = false) {
        const { opacity = 1, visible = true, onUpdate } = data;
        try {
            if (needsUpdate) {
                // const { points, normal, text } = data.renderProps;
                // const { polygon, border, roiText } = normal;
                if (!this.isMouseEnter) {
                    this.geometry = this.getGeometry(data);
                    this.material = this.getMaterial(data);
                    // this.material.opacity = opacity;
                    this.opacity.value = visible ? opacity : 0;
                }
                if (needUpdateProps) {
                    // this.visible = visible;
                    this.props = data;
                    if (onUpdate && typeof onUpdate === 'function') {
                        onUpdate(this);
                    }
                }
            }
            else {
                if (this.material && !this.isMouseEnter) {
                    // (this.material as Material).opacity = opacity;
                    this.props.opacity = visible ? opacity : 0;
                    this.opacity.value = visible ? opacity : 0;
                }
            }
        }
        catch (e) {
            console.log(e);
        }
        Object.keys(data).forEach((key) => {
            if (typeof data[key] === 'function') {
                this.props[key] = data[key];
            }
        });
    }
    getGeometry(data) {
        const roiTheme = data.type === 'fence' ? 'fence' : data.renderProps.theme;
        const roiConfig = getRoiConfig(roiTheme);
        const { wallHeight = roiConfig.wallHeight, borderWidth = roiConfig.borderWidth, parkWidth = roiConfig.parkWidth, parkIndex = this.boldLineNum, isPark = roiConfig.isPark } = data.renderProps;
        const uvArr = [];
        const posArr = [];
        const wallRoi = getWallRoiUvPos(this.vertices, wallHeight, this.normalVector);
        uvArr.push(...wallRoi.uv);
        posArr.push(...wallRoi.pos);
        const border = getBorderParkUvPos(this.vertices, borderWidth, this.normalVector, parkWidth, parkIndex, isPark);
        uvArr.push(...border.uv);
        posArr.push(...border.pos);
        const roi = getRoiUvPos(this.vertices);
        uvArr.push(...roi.uv);
        posArr.push(...roi.pos);
        const geometry = new THREE.BufferGeometry(); // 声明一个空几何体对象
        // 设置几何体attributes属性的位置position属性
        geometry.attributes.position = new THREE.BufferAttribute(new Float32Array(posArr), 3);
        // 设置几何体attributes属性的位置uv属性，确定三角面方向
        geometry.attributes.uv = new THREE.BufferAttribute(new Float32Array(uvArr), 2);
        geometry.computeVertexNormals(); // 计算顶点法向量，后续用于贴图
        return geometry;
    }
    getMaterial(data) {
        const roiTheme = data.type === 'fence' ? 'fence' : data.renderProps.theme;
        const roiConfig = getRoiConfig(roiTheme);
        const { lineOpacity = roiConfig.lineOpacity, color = roiConfig.color, isRoi = roiConfig.isRoi, roiColor = roiConfig.roiColor, roiOpacity = roiConfig.roiOpacity, isWall = roiConfig.isWall, wallColor = roiConfig.wallColor, wallOpacity = roiConfig.wallOpacity, isBorder = roiConfig.isBorder, borderColor = roiConfig.borderColor, isPark = roiConfig.isPark, parkColor = roiConfig.parkColor, isAnimation = roiConfig.isAnimation, isMoveLineAutoOpacity = roiConfig.isMoveLineAutoOpacity, isShowMoveLine = roiConfig.isShowMoveLine, isTwill = roiConfig.isTwill, } = data.renderProps;
        this.isAnimation = isAnimation;
        const material = new THREE.ShaderMaterial({
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            side: THREE.DoubleSide,
            lights: false,
            uniforms: {
                v_time: this.timeValue,
                opacity: this.opacity,
                line_opacity: {
                    value: lineOpacity,
                },
                v_color: {
                    value: new THREE.Color(color),
                },
                is_roi: {
                    value: isRoi,
                },
                roi_color: {
                    value: new THREE.Color(roiColor),
                },
                roi_opacity: {
                    value: roiOpacity,
                },
                is_wall: {
                    value: isWall,
                },
                wall_color: {
                    value: new THREE.Color(wallColor),
                },
                wall_opacity: {
                    value: wallOpacity,
                },
                is_border: {
                    value: isBorder,
                },
                border_color: {
                    value: new THREE.Color(borderColor),
                },
                border_uvh: {
                    value: UVConfig.border,
                },
                is_park: {
                    value: isPark,
                },
                park_uvh: {
                    value: UVConfig.park,
                },
                park_color: {
                    value: new THREE.Color(parkColor),
                },
                isShowMoveLine: {
                    value: isShowMoveLine,
                },
                isMoveLineAutoOpacity: {
                    value: isMoveLineAutoOpacity
                },
                isTwill: {
                    value: isTwill
                }
            },
        });
        return material;
    }
    getFont(size) {
        return `normal ${size}px Arial`;
    }
    getTextSize(size, text) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.font = this.getFont(size);
        const width = ctx.measureText(text).width;
        const height = ctx.measureText('M').width;
        return { width, height };
    }
    getBoldLineNum(points, boldPoints = []) {
        if (!boldPoints?.length)
            return -1;
        const begin = boldPoints[0];
        for (const i in points) {
            const curPoint = points[i];
            if (curPoint.toString() === begin.toString())
                return Number(i);
        }
        return -1;
    }
    onClick() {
        super.onClick();
    }
    dispose() {
        document.body.style.cursor = '';
        this.traverse(function (obj) {
            if (obj instanceof Mesh) {
                obj.geometry?.dispose();
                obj.material?.dispose();
            }
            else {
                if (obj.parent) {
                    obj?.parent.remove(obj);
                }
                obj.clear();
            }
        });
        this.parent?.remove(this);
        this.clear();
        window.cancelAnimationFrame(this.animateId);
    }
    onMouseEnter() {
        document.body.style.cursor = 'pointer';
        const Props = cloneDeep(this.props);
        Props.renderProps.normal = Props.renderProps.active;
        // Props.opacity = 1;
        this.opacity.value = 1;
        // this.update(Props, true, false);
        super.onMouseEnter();
        this.isMouseEnter = true;
    }
    onMouseLeave() {
        this.isMouseEnter = false;
        document.body.style.cursor = '';
        this.opacity.value = this.props?.opacity ?? 1;
        // this.update(this.props, true, true);
        super.onMouseLeave();
    }
}
export default ROI3D;
